import React, { useRef, useState } from 'react';
import { Button } from '../ui/button';
import emailjs from '@emailjs/browser';

type SubmitStatus = {
  type: 'success' | 'error' | null;
  message: string;
};

const ContactForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>({
    type: null,
    message: '',
  });
  
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formRef.current) return;
    
    setIsSubmitting(true);
    setSubmitStatus({ type: null, message: '' });

    emailjs.sendForm(
      import.meta.env.VITE_EMAILJS_SERVICE_ID || '',
      import.meta.env.VITE_EMAILJS_TEMPLATE_ID || '',
      formRef.current,
      import.meta.env.VITE_EMAILJS_PUBLIC_KEY || ''
    )
      .then((result) => {
        console.log('Email successfully sent!', result.text);
        setSubmitStatus({
          type: 'success',
          message: 'Ihre Nachricht wurde erfolgreich gesendet. Wir werden uns in Kürze bei Ihnen melden.'
        });
        formRef.current?.reset();
      })
      .catch((error) => {
        console.error('Failed to send email:', error);
        setSubmitStatus({
          type: 'error',
          message: 'Beim Senden Ihrer Nachricht ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut.'
        });
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <div className="bg-white p-8 rounded-lg border-2 border-primary/30 shadow-lg h-full flex flex-col">
      <h3 className="text-2xl font-semibold mb-6">Anfrage senden</h3>
      
      {submitStatus.type && (
        <div className={`mb-6 p-4 rounded-md ${
          submitStatus.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {submitStatus.message}
        </div>
      )}
      
      <form ref={formRef} className="space-y-4 flex flex-col flex-grow" onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1" htmlFor="user_name">Name</label>
            <input
              type="text"
              name="user_name"
              id="user_name"
              className="w-full p-2 border border-border rounded-md"
              placeholder="Ihr Name"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1" htmlFor="user_company">Unternehmen</label>
            <input
              type="text"
              name="user_company"
              id="user_company"
              className="w-full p-2 border border-border rounded-md"
              placeholder="Ihre Unternehmen"
            />
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1" htmlFor="user_email">E-Mail</label>
            <input
              type="email"
              name="user_email"
              id="user_email"
              className="w-full p-2 border border-border rounded-md"
              placeholder="Ihre E-Mail"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1" htmlFor="user_phone">Telefon</label>
            <input
              type="tel"
              name="user_phone"
              id="user_phone"
              className="w-full p-2 border border-border rounded-md"
              placeholder="Ihre Telefonnummer"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1" htmlFor="subject">Betreff</label>
          <input
            type="text"
            name="subject"
            id="subject"
            className="w-full p-2 border border-border rounded-md"
            placeholder="Betreff"
            required
          />
        </div>

        <div className="flex-grow flex flex-col">
          <label className="block text-sm font-medium mb-1" htmlFor="message">Nachricht</label>
          <textarea
            rows={5}
            name="message"
            id="message"
            maxLength={600}
            className="w-full p-2 border border-border rounded-md flex-grow min-h-[100px]"
            placeholder="Ihre Nachricht (max. 600 Zeichen)"
            required
          ></textarea>
        </div>

        <div className="mt-6 pt-2">
          <Button 
            type="submit" 
            className="w-full" 
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Wird gesendet...' : 'Anfrage absenden'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ContactForm;
